import { createContext, useContext } from 'react';
import en from '../i18n/en';
import es from '../i18n/es';

// ── Locale (staff-facing language) ───────────────────────────────────────────
// The UK staff portal (/staff) renders in English; the Spanish portal (/es)
// renders in Spanish. Shared components call useT() and look strings up by a
// dot-namespaced key, so the same component renders in the right language
// depending on which portal it's mounted under.
//
// Lookups fall back es → en → the key itself, so partial translation is safe.

const DICTS = { en, es };

const LocaleContext = createContext({ locale: 'en' });

export function LocaleProvider({ locale, children }) {
  return (
    <LocaleContext.Provider value={{ locale: DICTS[locale] ? locale : 'en' }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

function lookup(dict, key) {
  return key.split('.').reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

// Returns a translate function `t(key, vars?)`. `vars` fills `{name}` placeholders.
//
// Also on it:
//   t.value(group, v) — a known data value (a kit colour, an activity type…)
//                       from `values.<group>` keyed by its lower-cased text;
//                       anything unknown comes back unchanged.
//   t.dateLocale      — the locale for toLocaleDateString.
export function useT() {
  const { locale } = useLocale();
  function t(key, vars) {
    let s = lookup(DICTS[locale], key);
    if (s == null) s = lookup(DICTS.en, key);
    if (s == null) s = key;
    if (vars && typeof s === 'string') {
      for (const [k, v] of Object.entries(vars)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      }
    }
    return s;
  }
  t.value = (group, v) => {
    if (v == null || v === '') return v;
    return DICTS[locale]?.values?.[group]?.[String(v).trim().toLowerCase()] ?? v;
  };
  t.locale = locale;
  t.dateLocale = locale === 'es' ? 'es-ES' : 'en-GB';
  return t;
}

// For code outside React (pure helpers) that takes an optional `t`: plain
// English when none is given.
export function englishT(key, vars) {
  let s = lookup(DICTS.en, key) ?? key;
  if (vars && typeof s === 'string') {
    for (const [k, v] of Object.entries(vars)) s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
  }
  return s;
}
englishT.value = (group, v) => v;
englishT.locale = 'en';
englishT.dateLocale = 'en-GB';
